import React, { useState, useEffect, useCallback, useMemo ,useRef} from 'react';
import {
  Box,
  Typography,
  IconButton,
  Chip,
  CircularProgress
} from '@mui/material';
import { ArrowLeft } from 'lucide-react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import {
  createHubsLocation,
  updateHubsLocation,
  getHubsLocationById,
  createHubCity,
  getHubCitiesByState,
  getHubMatchingRetreats
} from '../../Services/HubsLocations/HubsLocationsApi';
import {
  categoryActiveApi,
  subCategoryActiveApiWithoutPagination
} from '../../Services/CategoryManagement/CategoryManagementApi';
import {
  getAtmospheres,
  getAllFeatures,
  getSurroundingAreas,
  getThingsInArea,
  getAllMeals
} from '../../Services/AddRetreatManagement/RetreatDetailsApi';
import { getAllLanguages } from '../../Services/ContentHub/GetlanguagesApi';
import { getRetreatListings } from '../../Services/Retreats/RetreatApis';
import { useGlobalSnackbar } from '../../Components/Common/GlobalSanckbar';
import statesData from '../../Assets/Data/states.json';
import { City } from 'country-state-city';
import './AddEditHubsLocation.css';

// Utilities & Helpers
import {
  slug,
  extractName,
  extractId,
  hubKey,
  countFor,
  MONTHS,
  computeRelatedHubs
} from './utils/hubsHelpers';

// Custom Hooks
import useApiDropdown from './hooks/useApiDropdown';

// Modular Components
import BasicDetailsCard from './components/BasicDetailsCard';
import RetreatFiltersCard from './components/RetreatFiltersCard';
import SeoCard from './components/SeoCard';
import DetailedContentCard from './components/DetailedContentCard';
import SuitabilityFaqsCard from './components/SuitabilityFaqsCard';
import LocationImageCard from './components/LocationImageCard';
import PlacementCard from './components/PlacementCard';
import PublishCard from './components/PublishCard';
import HubsDrawer from './components/HubsDrawer';
import PinRetreatDrawer from './components/PinRetreatDrawer';
import HubPreviewModal from './components/HubPreviewModal';

const AddEditHubsLocation = () => {
  const navigate = useNavigate();
  const { showSnackbar } = useGlobalSnackbar();
  const { id } = useParams();
  const location = useLocation();
  const editData = location.state?.hubLocation;
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [fetchingDetails, setFetchingDetails] = useState(false);

  // Status & Timestamps
  const [status, setStatus] = useState('Draft');
  const [savedAt, setSavedAt] = useState('Not saved yet');

  // Basic Details State
  const [category, setCategory] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [subCategory, setSubCategory] = useState('');
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState(null);
  const [state, setState] = useState('');
  const [town, setTown] = useState('');
  const [isOtherTown, setIsOtherTown] = useState(false);
  const [customTown, setCustomTown] = useState('');
  const [apiCities, setApiCities] = useState([]);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [citiesLoaded, setCitiesLoaded] = useState(false);
  const [isAddingCity, setIsAddingCity] = useState(false);
  const [hubName, setHubName] = useState('');
  const [heading, setHeading] = useState('');
  const [pageSlug, setPageSlug] = useState('');
  const [isManualUrlEdit, setIsManualUrlEdit] = useState(false);
  const [introLine, setIntroLine] = useState('');
  const [descriptionHtml, setDescriptionHtml] = useState('');

  // Lock states for auto-templates
  const [pendingCatId, setPendingCatId] = useState(null);
  const [pendingSubCatId, setPendingSubCatId] = useState(null);
  const [isCategoryWise, setIsCategoryWise] = useState(false);
  const [isLocationWise, setIsLocationWise] = useState(false);
  const [isDateWise, setIsDateWise] = useState(false);
  const [footerHubCategories, setFooterHubCategories] = useState(false);
  const [footerHubLocations, setFooterHubLocations] = useState(false);

  const [locks, setLocks] = useState({
    name: false,
    slug: false,
    h1: false,
    meta: false,
    metaD: false
  });

  // Filters State
  const [selectedFilters, setSelectedFilters] = useState({});
  const [sortOnPage, setSortOnPage] = useState('Price, low to high');

  // Live retreats from backend
  const [retreatsList, setRetreatsList] = useState([]);
  const [retreatsLoading, setRetreatsLoading] = useState(false);

  // SEO State
  const [metaTitle, setMetaTitle] = useState('');
  const [indexSetting, setIndexSetting] = useState('Index, follow');
  const [metaDescription, setMetaDescription] = useState('');
  const [canonicalOverride, setCanonicalOverride] = useState(false);
  const [customCanonical, setCustomCanonical] = useState('');
  const [targetKeyword, setTargetKeyword] = useState('');

  // Detailed Content
  const [detailedContentHtml, setDetailedContentHtml] = useState('');

  // Suitability & FAQs
  const [suitabilityItems, setSuitabilityItems] = useState([]);
  const [faqs, setFaqs] = useState([]);

  // Image & Placement
  const [imageFile, setImageFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [imageAltText, setImageAltText] = useState('');

  const [pHome, setPHome] = useState(false);
  const [pTab, setPTab] = useState('');
  const [pOrder, setPOrder] = useState('');

  // Related Hubs
  const [removedRel, setRemovedRel] = useState(new Set());
  const [addedRel, setAddedRel] = useState([]);

  // Drawers & Modal
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerKind, setDrawerKind] = useState('list'); // 'list' | 'pin' | 'hubs'
  const [modalOpen, setModalOpen] = useState(false);

  // References for rich text contenteditable
  const descEditorRef = useRef(null);
  const detailedEditorRef = useRef(null);
  const fileInputRef = useRef(null);
  const isFirstManualToggleRef = useRef(true);

  // -------------------------------------------------------------
  // API DROPDOWNS HOOKS (On-demand fetching on dropdown open)
  // -------------------------------------------------------------
  const categoryDropdown = useApiDropdown({
    fetcher: categoryActiveApi,
    normalizer: (items) => items.map((c) => ({ id: c.id, label: c.name || c.title || '' }))
  });

  const subCategoryDropdown = useApiDropdown({
    dep: selectedCategoryId,
    enabled: Boolean(selectedCategoryId),
    fetcher: () => subCategoryActiveApiWithoutPagination(selectedCategoryId),
    normalizer: (items) => items.map((s) => ({ id: s.id, label: s.name || s.title || '' }))
  });

  const atmosphereDropdown = useApiDropdown({
    fetcher: getAtmospheres,
    normalizer: (items) => items.map((a) => ({ id: a.id, label: a.title || a.atmosphereName || a.name || '' }))
  });

  const featuresDropdown = useApiDropdown({
    fetcher: getAllFeatures,
    normalizer: (items) => items.map((f) => ({ id: f.id, label: f.featureName || f.name || f.title || '' }))
  });

  const surroundingAreasDropdown = useApiDropdown({
    fetcher: getSurroundingAreas,
    normalizer: (items) => items.map((s) => ({ id: s.id, label: s.surroundingName || s.name || s.title || '' }))
  });

  const thingsInAreaDropdown = useApiDropdown({
    fetcher: getThingsInArea,
    normalizer: (items) => items.map((t) => ({ id: t.id, label: t.name || t.title || '' }))
  });

  const mealPlanDropdown = useApiDropdown({
    fetcher: getAllMeals,
    normalizer: (items) => items.map((m) => ({ id: m.id, label: m.mealName || m.name || m.title || '' }))
  });

  const languageDropdown = useApiDropdown({
    fetcher: getAllLanguages,
    normalizer: (items) => items.map((l) => ({ id: l.id, label: l.name || l.languageName || l.title || '' }))
  });

  // State selection helper
  const selectedStateObj = useMemo(() => {
    return (statesData?.states || []).find(
      (s) => s.name?.toLowerCase() === state?.toLowerCase() || s.isoCode === state
    );
  }, [state]);

  const stateIsoCode = selectedStateObj?.isoCode || '';

  // Helper arrays for States and Towns
  const availableStates = useMemo(() => {
    return (statesData?.states || []).map((s) => s.name).sort();
  }, []);

  // Fetch backend custom cities on-demand or state change
  const fetchBackendCities = useCallback(async (iso) => {
    if (!iso) {
      setApiCities([]);
      setCitiesLoaded(false);
      return;
    }
    try {
      setCitiesLoading(true);
      const res = await getHubCitiesByState(iso);
      if (res && res.success && Array.isArray(res.data)) {
        const names = res.data.map((c) => (typeof c === 'string' ? c : c.name)).filter(Boolean);
        setApiCities(names);
      } else {
        setApiCities([]);
      }
      setCitiesLoaded(true);
    } catch (err) {
      console.warn('Could not fetch backend cities for state:', iso, err);
      setApiCities([]);
    } finally {
      setCitiesLoading(false);
    }
  }, []);

  useEffect(() => {
    setCitiesLoaded(false);
    if (stateIsoCode) {
      fetchBackendCities(stateIsoCode);
    } else {
      setApiCities([]);
    }
  }, [stateIsoCode, fetchBackendCities]);

  const availableTowns = useMemo(() => {
    if (!state || !stateIsoCode) return ['Other'];
    const cscCities = City.getCitiesOfState('IN', stateIsoCode).map((c) => c.name);
    const allCities = [...cscCities, ...apiCities];
    const sortedCities = [...new Set(allCities)].sort();
    return [...sortedCities, 'Other'];
  }, [state, stateIsoCode, apiCities]);

  // Sync custom city mode in edit mode or when town is not in standard list
  useEffect(() => {
    if (town && availableTowns.length > 0) {
      const isStandardCity = availableTowns.includes(town) && town !== 'Other';
      if (!isStandardCity && !isOtherTown) {
        setIsOtherTown(true);
        setCustomTown(town);
      }
    }
  }, [town, availableTowns, isOtherTown]);

  // Handle adding custom city to backend
  const handleCreateCity = async () => {
    const cityName = customTown.trim();
    if (!cityName) {
      showSnackbar('Please enter a city name', 'warning');
      return;
    }
    if (!selectedStateObj) {
      showSnackbar('Please select a valid state first', 'warning');
      return;
    }

    try {
      setIsAddingCity(true);
      const payload = {
        name: cityName,
        stateName: selectedStateObj.name,
        isoCode: selectedStateObj.isoCode
      };
      const res = await createHubCity(payload);
      if (res && (res.success || res.data)) {
        showSnackbar('City created successfully', 'success');
        setApiCities((prev) => [...new Set([...prev, cityName])]);
        setTown(cityName);
        setIsOtherTown(false);
        setCustomTown('');
        if (!isManualUrlEdit) {
          setLocks((prev) => ({ ...prev, slug: false }));
        }
      } else {
        showSnackbar(res?.message || 'Failed to create city', 'error');
      }
    } catch (err) {
      console.error('Error creating city:', err);
      const errMsg = err.response?.data?.message || err.message || 'Error creating city';
      if (err.response?.status === 400 || (errMsg && errMsg.toLowerCase().includes('already exist'))) {
        setApiCities((prev) => [...new Set([...prev, cityName])]);
        setTown(cityName);
        setIsOtherTown(false);
        setCustomTown('');
        if (!isManualUrlEdit) {
          setLocks((prev) => ({ ...prev, slug: false }));
        }
        showSnackbar(errMsg, 'info');
      } else {
        showSnackbar(errMsg, 'error');
      }
    } finally {
      setIsAddingCity(false);
    }
  };

  // Fetch real retreats from backend using GET /api/v1/hubs-locations/retreats
  useEffect(() => {
    let isMounted = true;
    const loadRetreats = async () => {
      try {
        setRetreatsLoading(true);
        const params = {};
        if (state) params.state = state;
        const activeTown = town === 'Other' ? customTown : town;
        if (activeTown && activeTown.trim()) params.location = activeTown.trim();
        if (selectedCategoryId) params.categoryId = selectedCategoryId;

        const subObj = subCategoryDropdown.options.find((s) => (s.label || s.name) === subCategory);
        const subId = subObj?.id || selectedSubCategoryId || pendingSubCatId;
        if (subId && subCategory && !subCategory.startsWith('All')) {
          params.subCategoryId = subId;
        }

        const catStr = typeof category === 'string' ? category : String(category || '');
        params.placementType = catStr.toLowerCase().includes('training') ? 'training' : 'retreat';

        if (selectedFilters.atmosphereId) params.atmosphereId = selectedFilters.atmosphereId;
        if (selectedFilters.featuresId) params.featuresId = selectedFilters.featuresId;
        if (selectedFilters.mealPlanId) params.mealPlanId = selectedFilters.mealPlanId;
        if (selectedFilters.surroundingAreasId) params.surroundingAreasId = selectedFilters.surroundingAreasId;
        if (selectedFilters.thingsInAreaId) params.thingsInAreaId = selectedFilters.thingsInAreaId;
        if (selectedFilters.languageId) params.languageId = selectedFilters.languageId;
        params.page = 1;
        params.limit = 50;

        const res = await getHubMatchingRetreats(params);
        let list = [];
        if (res && res.success && Array.isArray(res.data)) {
          list = res.data;
        } else if (res && res.success && Array.isArray(res.data?.retreats)) {
          list = res.data.retreats;
        } else if (res && Array.isArray(res.data)) {
          list = res.data;
        } else if (Array.isArray(res)) {
          list = res;
        }

        if (isMounted) {
          setRetreatsList(list);
        }
      } catch (e) {
        console.warn('Could not fetch retreats from hubs-locations/retreats, trying fallback:', e);
        try {
          const fallbackRes = await getRetreatListings(1, 100);
          if (isMounted && fallbackRes && fallbackRes.success && Array.isArray(fallbackRes.data)) {
            setRetreatsList(fallbackRes.data);
          }
        } catch (fallbackErr) {
          console.error('Retreat fallback fetch failed:', fallbackErr);
        }
      } finally {
        if (isMounted) setRetreatsLoading(false);
      }
    };

    const timer = setTimeout(() => {
      loadRetreats();
    }, 250);

    return () => {
      isMounted = false;
      clearTimeout(timer);
    };
  }, [
    state,
    town,
    customTown,
    category,
    selectedCategoryId,
    subCategory,
    selectedSubCategoryId,
    pendingSubCatId,
    subCategoryDropdown.options,
    selectedFilters.atmosphereId,
    selectedFilters.featuresId,
    selectedFilters.mealPlanId,
    selectedFilters.surroundingAreasId,
    selectedFilters.thingsInAreaId,
    selectedFilters.languageId,
    drawerOpen,
    drawerKind
  ]);

  // Safe parse array helper
  const safeParseArray = (val, defaultVal = []) => {
    if (!val) return defaultVal;
    if (Array.isArray(val)) return val;
    try {
      const parsed = JSON.parse(val);
      return Array.isArray(parsed) ? parsed : defaultVal;
    } catch {
      return defaultVal;
    }
  };

  // Populate data when loading an existing hub in edit mode
  const populateFields = useCallback((data) => {
    if (!data) return;

    if (data.hubName) setHubName(data.hubName);
    else if (data.name) setHubName(data.name);

    if (data.heading) setHeading(data.heading);

    if (data.pageUrl) {
      const pageUrlStr = typeof data.pageUrl === 'string' ? data.pageUrl : String(data.pageUrl || '');
      setPageSlug(pageUrlStr.startsWith('/') ? pageUrlStr : '/' + pageUrlStr);
    } else if (data.slug) {
      const slugStr = typeof data.slug === 'string' ? data.slug : String(data.slug || '');
      setPageSlug(slugStr.startsWith('/') ? slugStr : '/' + slugStr);
    }

    if (data.introLine) setIntroLine(data.introLine);

    if (data.description) {
      setDescriptionHtml(data.description);
    }

    if (data.detailedContent) {
      setDetailedContentHtml(data.detailedContent);
    }

    if (data.images) {
      const img = Array.isArray(data.images) ? data.images[0] : data.images;
      setPreviewImage(img);
    } else if (data.image) {
      setPreviewImage(data.image);
    }

    if (data.imageAltText) setImageAltText(data.imageAltText);
    else if (data.imageAlt) setImageAltText(data.imageAlt);

    if (data.metaTitle) setMetaTitle(data.metaTitle);
    if (data.metaDescription) setMetaDescription(data.metaDescription);

    if (data.metaKeywords) setTargetKeyword(data.metaKeywords);
    else if (data.targetKeyword) setTargetKeyword(data.targetKeyword);

    if (data.indexFollow) {
      setIndexSetting(data.indexFollow.toLowerCase().includes('noindex') ? 'Noindex, follow' : 'Index, follow');
    } else if (data.indexSetting) {
      setIndexSetting(data.indexSetting);
    }

    if (data.canonicalUrl) {
      setCustomCanonical(data.canonicalUrl);
      setCanonicalOverride(true);
    }

    if (data.status) {
      const formattedStatus = data.status.charAt(0).toUpperCase() + data.status.slice(1).toLowerCase();
      setStatus(formattedStatus === 'Published' ? 'Published' : 'Draft');
    }

    if (data.placement) {
      try {
        const pl = typeof data.placement === 'string' ? JSON.parse(data.placement) : data.placement;
        if (pl.showOnHomepage !== undefined) setPHome(Boolean(pl.showOnHomepage));
        if (pl.tab) setPTab(pl.tab || '');
        if (pl.order !== undefined && pl.order !== null) setPOrder(pl.order);
      } catch (e) { }
    } else {
      if (data.showOnHomepage !== undefined) setPHome(Boolean(data.showOnHomepage));
      if (data.tab) setPTab(data.tab || '');
      if (data.order !== undefined && data.order !== null) setPOrder(data.order);
    }

    if (data.isCategoryWise !== undefined) {
      setIsCategoryWise(String(data.isCategoryWise) === 'true');
    }
    if (data.isLocationWise !== undefined) {
      setIsLocationWise(String(data.isLocationWise) === 'true');
    }
    if (data.isDateWise !== undefined) {
      setIsDateWise(String(data.isDateWise) === 'true');
    }
    if (data.footerHubCategories !== undefined) {
      setFooterHubCategories(String(data.footerHubCategories) === 'true');
    }
    if (data.footerHubLocations !== undefined) {
      setFooterHubLocations(String(data.footerHubLocations) === 'true');
    }

    const parsedSuit = safeParseArray(data.suitabilityItems, []);
    setSuitabilityItems(
      parsedSuit
        .map((item) => extractName(item) || (typeof item === 'string' ? item : ''))
        .filter(Boolean)
    );

    const parsedFaqs = safeParseArray(data.faqs, []);
    setFaqs(
      parsedFaqs.map((f) => {
        if (Array.isArray(f)) return { question: extractName(f[0]), answer: extractName(f[1]) };
        return {
          question: extractName(f?.question || f?.q),
          answer: extractName(f?.answer || f?.a)
        };
      })
    );

    if (data.state) setState(extractName(data.state));
    if (data.location) setTown(extractName(data.location));
    else if (data.town) setTown(extractName(data.town));
    else if (data.locationName && !data.state) {
      const locStr = extractName(data.locationName);
      const parts = locStr.split(',').map((s) => s.trim());
      if (parts.length > 1) {
        setTown(parts[0]);
        setState(parts[1]);
      } else {
        setState(parts[0]);
      }
    }

    const catName = extractName(data.category) || extractName(data.categoryId);
    const catId = extractId(data.categoryId) || extractId(data.category);
    if (catId) {
      setPendingCatId(catId);
      setSelectedCategoryId(catId);
    }
    if (catName) {
      setCategory(catName);
    }

    const subCatName = extractName(data.subCategory) || extractName(data.subCategoryId);
    const subCatId = extractId(data.subCategoryId) || extractId(data.subCategory);
    if (subCatId) {
      setPendingSubCatId(subCatId);
      setSelectedSubCategoryId(subCatId);
    }
    if (subCatName) {
      setSubCategory(subCatName);
    }

    // Filters and filter attributes
    let loadedFilters = {};
    if (data.filters) {
      try {
        loadedFilters = typeof data.filters === 'string' ? JSON.parse(data.filters) : data.filters;
      } catch (e) { }
    }
    if (loadedFilters && typeof loadedFilters === 'object') {
      Object.keys(loadedFilters).forEach((k) => {
        const v = loadedFilters[k];
        if (typeof v === 'object' && v !== null && !Array.isArray(v)) {
          loadedFilters[k] = extractName(v);
        } else if (Array.isArray(v)) {
          loadedFilters[k] = v.map((item) => extractName(item) || item);
        }
      });
    }
    if (data.dates) loadedFilters.dates = extractName(data.dates);
    if (data.months) loadedFilters.month = extractName(data.months);
    if (data.duration) loadedFilters.dur = extractName(data.duration);
    if (data.priceRange) loadedFilters.price = extractName(data.priceRange);
    if (data.atmosphere) loadedFilters.atm = extractName(data.atmosphere);
    if (data.atmosphereId) loadedFilters.atmosphereId = extractId(data.atmosphereId) || extractName(data.atmosphereId);
    if (data.features) loadedFilters.feat = extractName(data.features);
    if (data.featuresId) loadedFilters.featuresId = extractId(data.featuresId) || extractName(data.featuresId);
    if (data.surroundingAreas) loadedFilters.sur = extractName(data.surroundingAreas);
    if (data.surroundingAreasId) loadedFilters.surroundingAreasId = extractId(data.surroundingAreasId) || extractName(data.surroundingAreasId);
    if (data.thingsInArea) loadedFilters.near = extractName(data.thingsInArea);
    if (data.thingsInAreaId) loadedFilters.thingsInAreaId = extractId(data.thingsInAreaId) || extractName(data.thingsInAreaId);
    if (data.mealPlan || data.meals) loadedFilters.food = extractName(data.mealPlan || data.meals);
    if (data.mealPlanId) loadedFilters.mealPlanId = extractId(data.mealPlanId) || extractName(data.mealPlanId);
    if (data.language || data.languages) loadedFilters.lang = extractName(data.language || data.languages);
    if (data.languageId) loadedFilters.languageId = extractId(data.languageId) || extractName(data.languageId);
    if (data.skill || data.skillLevel) loadedFilters.skill = extractName(data.skill || data.skillLevel);
    if (data.age || data.ageGroup) loadedFilters.age = extractName(data.age || data.ageGroup);
    if (data.ageGroupId) loadedFilters.ageGroupId = extractId(data.ageGroupId) || extractName(data.ageGroupId);
    if (data.experienceType) {
      const parsedExp = safeParseArray(data.experienceType, []).map((e) => extractName(e) || e);
      if (parsedExp.length > 0) loadedFilters.experienceType = parsedExp;
    }
    setSelectedFilters((prev) => ({ ...prev, ...loadedFilters }));

    // Lock all template fields on edit load
    setLocks({
      name: true,
      slug: true,
      h1: true,
      meta: true,
      metaD: true
    });
  }, []);

  // Fetch initial data in edit mode
  useEffect(() => {
    const loadInitialData = async () => {
      if (isEdit) {
        try {
          setFetchingDetails(true);
          const res = await getHubsLocationById(id);
          if (res && (res.success || res.data)) {
            populateFields(res.data || res);
          } else {
            showSnackbar('Failed to load hub location details', 'error');
          }
        } catch (err) {
          showSnackbar('Error fetching hub location', 'error');
        } finally {
          setFetchingDetails(false);
        }
      }
    };
    loadInitialData();
  }, [id, isEdit, populateFields, showSnackbar]);

  // Name derivation helpers
  const pageName = useMemo(() => {
    const subCatStr = extractName(subCategory);
    const catStr = extractName(category);
    return subCatStr && !subCatStr.startsWith('All') ? subCatStr : catStr;
  }, [category, subCategory]);

  const placeName = useMemo(() => {
    const activeTown = town === 'Other' ? customTown : town;
    const tStr = extractName(activeTown);
    const sStr = extractName(state);
    return tStr ? (sStr ? `${tStr}, ${sStr}` : tStr) : sStr;
  }, [town, customTown, state]);

  // Sync category label when options load if only ID was stored
  useEffect(() => {
    if (categoryDropdown.options.length > 0 && selectedCategoryId) {
      const match = categoryDropdown.options.find((c) => String(c.id) === String(selectedCategoryId));
      if (match && (!category || category === String(selectedCategoryId) || typeof category === 'object')) {
        setCategory(match.label || match.name || '');
      }
    }
  }, [categoryDropdown.options, selectedCategoryId, category]);

  // Sync subcategory label when options load
  useEffect(() => {
    if (subCategoryDropdown.options.length > 0 && pendingSubCatId) {
      const match = subCategoryDropdown.options.find((s) => String(s.id) === String(pendingSubCatId));
      if (match && (!subCategory || subCategory === String(pendingSubCatId) || typeof subCategory === 'object')) {
        setSubCategory(match.label || match.name || '');
        setSelectedSubCategoryId(match.id);
      }
    }
  }, [subCategoryDropdown.options, pendingSubCatId, subCategory]);

    // Apply templates function
    const applyTemplates = useCallback(
      (force = false) => {
        const catClean = extractName(category);
        const subClean = extractName(subCategory);
        const stClean = extractName(state);
        const activeTown = town === 'Other' ? customTown : town;
        const tClean = activeTown ? extractName(activeTown).trim() : '';

        // Build URL segments locally on every invocation
        const parts = [];

        const namePart = subClean && !subClean.startsWith('All') ? subClean : catClean;

        // Build category/location URL segments
        if (namePart) {
          parts.push(slug(namePart));
        }

        if (stClean) {
          parts.push(slug(stClean));
        }

        if (tClean && tClean !== 'Other') {
          parts.push(slug(tClean));
        }

        // Append the selected month, if valid
        const m = selectedFilters.month;
        if (m && MONTHS.includes(m)) {
          parts.push(slug(m));
        }

        // Use the Hub Name only when no other URL segments exist
        if (parts.length === 0 && hubName) {
          const hSlug = slug(hubName);
          if (hSlug) {
            parts.push(hSlug);
          }
        }

        // Update URL unless it is locked
        if (parts.length > 0) {
          const generatedSlug = `/c/${parts.join('/')}`;
          if (!locks.slug || force) setPageSlug(generatedSlug);
        } else if (!isEdit && (!locks.slug || force)) {
          setPageSlug('');
        }

        // 2. Hub Name, Heading (H1), Meta Title and Description suggestions
        const locPart = tClean && tClean !== 'Other'
          ? (stClean ? `${tClean}, ${stClean}` : tClean)
          : stClean;

        if (namePart || locPart) {
          const suggestedName = namePart && locPart
            ? `${namePart} in ${locPart}`
            : (namePart || locPart);

          if (!locks.name || force) setHubName(suggestedName);
          if (!locks.h1 || force) setHeading(suggestedName ? `Best ${suggestedName}` : '');

          if (!locks.meta || force) {
            const mtLoc = tClean && tClean !== 'Other'
              ? `${tClean}, ${stClean}`
              : (stClean ? `${stClean}, India` : 'India');
            setMetaTitle(namePart ? `Best ${namePart} in ${mtLoc} | Heal at India` : (suggestedName ? `${suggestedName} | Heal at India` : ''));
          }

          if (!locks.metaD || force) {
            if (namePart && locPart) {
              setMetaDescription(
                `Find ${namePart.toLowerCase()} in ${locPart}. Compare doctor led programmes, dates and prices at verified centres and book with free cancellation on Heal at India.`
              );
            }
          }
        }
      },
      [category, subCategory, state, town, customTown, hubName, selectedFilters.month, locks.slug, locks.name, locks.h1, locks.meta, locks.metaD, isEdit]
    );

  // Category and SubCategory change handlers
  const handleCategoryChange = (newCat) => {
    const cleanCat = extractName(newCat);
    setCategory(cleanCat);
    const catObj = categoryDropdown.options.find((c) => (c.label || c.name) === cleanCat);
    setSelectedCategoryId(catObj?.id || null);
    setSubCategory('');
    if (!isManualUrlEdit) {
      setLocks((prev) => ({ ...prev, slug: false }));
    }
  };

  const handleSubCategoryChange = (newSub) => {
    setSubCategory(extractName(newSub));
    if (!isManualUrlEdit) {
      setLocks((prev) => ({ ...prev, slug: false }));
    }
  };

  const handleStateChange = (newState) => {
    setState(extractName(newState));
    setTown('');
    setIsOtherTown(false);
    setCustomTown('');
    if (!isManualUrlEdit) {
      setLocks((prev) => ({ ...prev, slug: false }));
    }
  };

  const handleTownChange = (newTown) => {
    setTown(extractName(newTown));
    if (!isManualUrlEdit) {
      setLocks((prev) => ({ ...prev, slug: false }));
    }
  };

  const handleHubNameChange = (newHubName) => {
    setHubName(newHubName);
    setLocks((prev) => ({ ...prev, name: true, ...(isManualUrlEdit ? {} : { slug: false }) }));
  };

  // Re-run template updates when dependencies change
  useEffect(() => {
    applyTemplates();
  }, [hubName, category, subCategory, state, town, selectedFilters, applyTemplates]);

  // Regenerate URL when switching from manual to automatic mode
  useEffect(() => {
    if (isFirstManualToggleRef.current) {
      isFirstManualToggleRef.current = false;
      return;
    }
    if (!isManualUrlEdit) {
      applyTemplates(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isManualUrlEdit]);

  // Generic Filter Change Handler
  const handleFilterChange = (key, val, options = [], idKey = null) => {
    if (!isManualUrlEdit) {
      setLocks((prev) => ({ ...prev, slug: false }));
    }
    setSelectedFilters((prev) => {
      const copy = { ...prev };
      const cleanVal = extractName(val);
      if (cleanVal) {
        copy[key] = cleanVal;
        if (idKey && options.length > 0) {
          const found = options.find((o) => (o.label || o.name || o.title) === cleanVal);
          if (found?.id) copy[idKey] = String(found.id);
        }
      } else {
        delete copy[key];
        if (idKey) delete copy[idKey];
      }
      return copy;
    });
  };

  // Matching Retreats Calculation using real retreatsList safely
  const matchingRetreats = useMemo(() => {
    if (!Array.isArray(retreatsList) || retreatsList.length === 0) return [];
    const stStr = extractName(state);
    const townStr = extractName(town);
    let list = retreatsList;

    if (sortOnPage.startsWith('Price')) {
      list = [...list].sort((a, b) => (a.id || 0) - (b.id || 0));
    } else if (sortOnPage.startsWith('Newest')) {
      list = [...list].sort((a, b) => (b.id || 0) - (a.id || 0));
    }

    return list;
  }, [state, town, sortOnPage, retreatsList]);

  // Related Hubs computation
  const relatedGroups = useMemo(() => {
    const catStr = typeof category === 'string' ? category : String(category || '');
    const subCatStr = typeof subCategory === 'string' ? subCategory : String(subCategory || '');
    const stateStr = typeof state === 'string' ? state : String(state || '');
    const townStr = typeof town === 'string' ? town : String(town || '');
    return computeRelatedHubs({
      category: catStr,
      subCategory: subCatStr,
      state: stateStr,
      town: townStr,
      month: selectedFilters.month,
      removedSet: removedRel,
      addedList: addedRel,
      retreatsList
    });
  }, [category, subCategory, state, town, selectedFilters.month, removedRel, addedRel, retreatsList]);

  // Unique list of all hubs for the Related Hubs Drawer
  const allAvailableHubs = useMemo(() => {
    if (!Array.isArray(retreatsList) || retreatsList.length === 0) return [];
    const hubs = [];
    const seenHubKeys = new Set();
    retreatsList.forEach((r) => {
      [
        [r.subCategory?.name, r.location?.state, ''],
        [r.subCategory?.name, r.location?.state, r.location?.city]
      ].forEach(([s, st, t]) => {
        if (!s || !st) return;
        const k = hubKey(s, st, t);
        if (seenHubKeys.has(k)) return;
        seenHubKeys.add(k);
        hubs.push({
          t: `${s} in ${t || st}`,
          u: k,
          n: countFor(s, st, t, retreatsList),
          sub: s
        });
      });
    });
    hubs.sort((a, b) => a.t.localeCompare(b.t));
    return hubs;
  }, [retreatsList]);

  // Image Upload Handling
  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.size > 5 * 1024 * 1024) {
        showSnackbar('Image must be smaller than 5MB', 'error');
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Image Remove Handling
  const handleRemoveImage = (e) => {
    e.stopPropagation();
    setImageFile(null);
    setPreviewImage(null);
    setImageAltText('');
  };

  // Related Hubs actions
  const removeRelHub = (url) => {
    setRemovedRel((prev) => new Set([...prev, url]));
    setAddedRel((prev) => prev.filter((x) => x.u !== url));
  };

  const addRelHub = (u, t, n, g) => {
    setRemovedRel((prev) => {
      const copy = new Set(prev);
      copy.delete(u);
      return copy;
    });
    if (!addedRel.find((x) => x.u === u)) {
      setAddedRel((prev) => [...prev, { u, t, n, g }]);
      showSnackbar(`Added to ${g} retreats`, 'success');
    }
    setDrawerOpen(false);
  };

  const openDrawer = (kind) => {
    setDrawerKind(kind);
    setDrawerOpen(true);
  };

  // Checklist Validation
  const checklist = useMemo(() => {
    const mtLen = metaTitle.length;
    const mdLen = metaDescription.length;
    const hasH1AndIntro = !!heading.trim() && !!introLine.trim();
    const hasImageAndAlt = !previewImage && !imageFile ? true : ((!!previewImage || !!imageFile) && !!imageAltText.trim());
    const hasLiveMatch = matchingRetreats.length >= 1;
    const isUniqueUrl =
      !['/c/ayurveda-retreats/kerala/kochi'].includes(pageSlug) || status === 'Published';
    const hasEnoughSuitAndFaqs = suitabilityItems.length >= 3 && faqs.length >= 3;

    return [
      { label: 'Meta title 50 to 60 characters', valid: mtLen >= 50 && mtLen <= 60 },
      { label: 'Meta description 150 to 160 characters', valid: mdLen >= 150 && mdLen <= 160 },
      { label: 'H1 and intro line filled', valid: hasH1AndIntro },
      { label: 'Image with ALT text (optional)', valid: hasImageAndAlt },
      { label: 'At least 1 live retreat matches', valid: hasLiveMatch },
      { label: 'Page URL is unique', valid: isUniqueUrl },
      { label: 'At least 3 suitability items and 3 FAQs', valid: hasEnoughSuitAndFaqs }
    ];
  }, [
    metaTitle,
    metaDescription,
    heading,
    introLine,
    previewImage,
    imageFile,
    imageAltText,
    matchingRetreats.length,
    pageSlug,
    status,
    suitabilityItems.length,
    faqs.length
  ]);

  const canPublish = (!!hubName.trim() && !!state.trim() && !!heading.trim()) || checklist.every((c) => c.valid) || status === 'Published' || isEdit;

  // Form Submit / Save Draft / Publish
  const handleSave = async (newStatus = 'Draft') => {
    if (newStatus === 'Published' && !canPublish) {
      showSnackbar('Please satisfy all checklist requirements before publishing', 'error');
      return;
    }

    try {
      setLoading(true);

      // Pre-save custom city to backend if user didn't explicitly click Add City
      if (isOtherTown && customTown.trim() && selectedStateObj) {
        try {
          await createHubCity({
            name: customTown.trim(),
            stateName: selectedStateObj.name,
            isoCode: selectedStateObj.isoCode
          });
          setApiCities((prev) => [...new Set([...prev, customTown.trim()])]);
        } catch (e) {
          console.warn('Could not pre-save custom city:', e);
        }
      }

      const matchedApiCat = categoryDropdown.options.find((c) => (c.label || c.name) === category);
      const matchedApiSub = subCategoryDropdown.options.find((s) => (s.label || s.name) === subCategory);
      const catId = matchedApiCat ? String(matchedApiCat.id) : (selectedCategoryId ? String(selectedCategoryId) : (pendingCatId ? String(pendingCatId) : '1'));
      const subCatId = matchedApiSub ? String(matchedApiSub.id) : (pendingSubCatId ? String(pendingSubCatId) : '2');

      const catStr = typeof category === 'string' ? category : String(category || '');
      const placementType = catStr.toLowerCase().includes('training') ? 'training' : 'retreat';
      const cleanSlug = pageSlug ? pageSlug.replace(/^\/+/, '') : '';
      const formattedStatus = (newStatus || 'published').toLowerCase();
      const locationVal = town ? town.trim() : (state ? state.trim() : '');

      const formData = new FormData();

      // Core fields from POST /api/v1/hubs-locations contract
      formData.append('state', state || '');
      formData.append('location', locationVal);
      formData.append('hubName', hubName.trim());
      formData.append('heading', heading.trim());
      formData.append('pageUrl', cleanSlug);
      formData.append('introLine', introLine.trim());
      formData.append('description', descriptionHtml.trim());

      // Images (multipart)
      if (imageFile) {
        formData.append('images', imageFile);
      } else if (previewImage && typeof previewImage === 'string' && !previewImage.startsWith('blob:')) {
        formData.append('existingImage', previewImage);
      } else if (isEdit && !previewImage && !imageFile) {
        formData.append('removeImage', 'true');
      }

      formData.append('placementType', placementType);
      formData.append('status', formattedStatus);
      formData.append('categoryId', catId);
      if (subCatId) {
        formData.append('subCategoryId', subCatId);
      }

      // Filter attributes
      formData.append('dates', selectedFilters.dates || selectedFilters.date || '');
      formData.append('months', selectedFilters.month || selectedFilters.months || '');
      formData.append('duration', selectedFilters.dur || selectedFilters.duration || '');
      formData.append('priceRange', selectedFilters.price || selectedFilters.priceRange || '');

      // Filter IDs
      const atmId = selectedFilters.atmosphereId || (selectedFilters.atm ? String(Math.max(1, atmosphereDropdown.options.findIndex((o) => (o.label || o.name) === selectedFilters.atm) + 1)) : '1');
      const surId = selectedFilters.surroundingAreasId || (selectedFilters.sur ? String(Math.max(1, surroundingAreasDropdown.options.findIndex((o) => (o.label || o.name) === selectedFilters.sur) + 1)) : '1');
      const nearId = selectedFilters.thingsInAreaId || (selectedFilters.near ? String(Math.max(1, thingsInAreaDropdown.options.findIndex((o) => (o.label || o.name) === selectedFilters.near) + 1)) : '1');
      const foodId = selectedFilters.mealPlanId || (selectedFilters.food ? String(Math.max(1, mealPlanDropdown.options.findIndex((o) => (o.label || o.name) === selectedFilters.food) + 1)) : '1');
      const featId = selectedFilters.featuresId || (selectedFilters.feat ? String(Math.max(1, featuresDropdown.options.findIndex((o) => (o.label || o.name) === selectedFilters.feat) + 1)) : '1');
      const ageId = selectedFilters.ageGroupId || '1';
      const langId = selectedFilters.languageId || (selectedFilters.lang ? String(Math.max(1, languageDropdown.options.findIndex((o) => (o.label || o.name) === selectedFilters.lang) + 1)) : '1');

      formData.append('atmosphereId', atmId);
      formData.append('surroundingAreasId', surId);
      formData.append('thingsInAreaId', nearId);
      formData.append('mealPlanId', foodId);
      formData.append('featuresId', featId);
      formData.append('ageGroupId', ageId);
      formData.append('languageId', langId);

      // Boolean placement flags
      formData.append('isCategoryWise', isCategoryWise.toString());
      formData.append('isLocationWise', isLocationWise.toString());
      formData.append('isDateWise', isDateWise.toString());
      formData.append('footerHubCategories', footerHubCategories.toString());
      formData.append('footerHubLocations', footerHubLocations.toString());

      // Experience Type JSON array
      let expList = [];
      if (Array.isArray(selectedFilters.experienceType) && selectedFilters.experienceType.length > 0) {
        expList = selectedFilters.experienceType;
      } else {
        const expSet = new Set();
        const catStr = typeof category === 'string' ? category : String(category || '');
        const subCatStr = typeof subCategory === 'string' ? subCategory : String(subCategory || '');
        if (catStr && catStr !== 'All' && !catStr.startsWith('All')) expSet.add(catStr);
        if (subCatStr && subCatStr !== 'All' && !subCatStr.startsWith('All')) expSet.add(subCatStr);
        if (selectedFilters.atm) expSet.add(selectedFilters.atm);
        if (expSet.size === 0) expSet.add('Yoga');
        expList = Array.from(expSet);
      }
      formData.append('experienceType', JSON.stringify(expList));

      // FAQs & Suitability
      formData.append('faqs', JSON.stringify(faqs));
      formData.append('suitabilityItems', JSON.stringify(suitabilityItems));

      // SEO fields
      formData.append('metaTitle', metaTitle.trim());
      formData.append('metaDescription', metaDescription.trim());
      formData.append('metaKeywords', targetKeyword.trim());
      const cleanIndexFollow = (indexSetting || 'index,follow').toLowerCase().replace(/\s+/g, '');
      formData.append('indexFollow', cleanIndexFollow);

      const generatedCanonical = `https://www.healatindia.com/hubs-locations/${cleanSlug}`;
      const finalCanonical = canonicalOverride && customCanonical.trim() ? customCanonical.trim() : generatedCanonical;
      formData.append('canonicalUrl', finalCanonical);

      // Detailed Content
      formData.append('detailedContent', detailedContentHtml.trim());

      // Backward compatibility fields
      formData.append('name', hubName.trim());
      formData.append('locationName', placeName.trim());
      formData.append('type', placementType);
      formData.append('slug', pageSlug);
      formData.append('town', town || '');
      formData.append('targetKeyword', targetKeyword.trim());
      if (previewImage || imageFile) {
        formData.append('imageAltText', imageAltText || '');
      }
      formData.append('indexSetting', indexSetting);
      formData.append(
        'placement',
        JSON.stringify({
          showOnHomepage: Boolean(pHome),
          tab: pTab || '',
          order: pOrder !== '' && pOrder !== null ? Number(pOrder) : null
        })
      );
      formData.append('filters', JSON.stringify(selectedFilters));

      let res;
      if (isEdit) {
        res = await updateHubsLocation(id, formData);
      } else {
        res = await createHubsLocation(formData);
      }

      if (res && (res.success || res.status === 'success' || res.data || res.id)) {
        setStatus(newStatus === 'published' ? 'Published' : newStatus);
        const timeStr = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        setSavedAt(`${newStatus} at ${timeStr}`);

        let successMsg;
        if (isEdit) {
          if (newStatus.toLowerCase() === 'published') {
            successMsg = res.message || res.data?.message || 'Published successfully! Hub location is now live.';
          } else {
            successMsg = res.message || res.data?.message || 'Updated successfully! Hub location has been updated.';
          }
        } else {
          if (newStatus.toLowerCase() === 'published') {
            successMsg = res.message || res.data?.message || 'Published successfully! Hub location is now live.';
          } else {
            successMsg = res.message || res.data?.message || 'Draft saved successfully!';
          }
        }

        showSnackbar(successMsg, 'success');

        if (!isEdit) {
          const createdId = res.id || (res.data && res.data.id);
          if (createdId) {
            setTimeout(() => {
              navigate(`/hubs-locations/edit/${createdId}`, { replace: true });
            }, 1000);
          }
        }
      } else {
        setStatus(newStatus);
        const timeStr = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        setSavedAt(`${newStatus} saved at ${timeStr}`);

        const fallbackMsg = res?.message || `${newStatus} saved!`;
        showSnackbar(fallbackMsg, res?.success ? 'success' : 'info');
      }
    } catch (err) {
      console.error('Backend API save encountered response:', err);
      const errMsg = err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Failed to save';
      showSnackbar(`Error: ${errMsg}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleUnpublish = () => {
    if (window.confirm('Unpublish this page? The URL will return 410 until it is published again.')) {
      setStatus('Draft');
      setSavedAt('Unpublished at ' + new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
      showSnackbar('Unpublished. Removed from sitemap, homepage, and footer hubs.', 'info');
    }
  };

  if (fetchingDetails) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: '#FFBD65' }} />
      </Box>
    );
  }

  // Canonical calculation
  const canonicalUrl = canonicalOverride && customCanonical ? customCanonical : (pageSlug ? `https://healatindia.com${pageSlug}` : '');

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 2.5 }, minHeight: '100vh', color: '#1E293B' }}>
      {/* Title Bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <IconButton
            size="small"
            onClick={() => navigate('/hubs-locations')}
            sx={{
              border: '1px solid #E2E8F0',
              borderRadius: '6px',
              p: '6px',
              backgroundColor: '#FFFFFF',
              color: '#334155',
              '&:hover': { borderColor: '#CBD5E1', backgroundColor: '#F8FAFC' }
            }}
          >
            <ArrowLeft size={18} />
          </IconButton>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h5" sx={{ fontSize: 17, fontWeight: 600, color: '#1E293B', fontFamily: 'inherit' }}>
                {isEdit ? 'Edit Hub / Location Page' : 'Add Hub / Location Page'}
              </Typography>
              <Chip
                label={status === 'Published' ? 'Published' : 'Draft'} size="small"
                sx={{
                  backgroundColor: status === 'Published' ? '#F0FDF4' : '#FFFDF9',
                  color: status === 'Published' ? '#2E7D32' : '#803927',
                  border: `1px solid ${status === 'Published' ? '#BBF7D0' : '#F6D9A8'}`,
                  fontWeight: 600,
                  fontSize: 11,
                  height: 22,
                  borderRadius: '4px'
                }}
              />
            </Box>
            <Typography variant="caption" sx={{ color: '#64748B', fontSize: 12 }}>
              {isEdit ? 'Update landing page for retreats in this category and area' : 'Create a landing page for retreats in a category and area'} · {savedAt}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Main Grid: Left Column & Right Sidebar */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 300px' },
          gap: 2,
          alignItems: 'start'
        }}
      >
        {/* LEFT COLUMN */}
        <Box>
          {/* 1. Basic Details Card */}
          <BasicDetailsCard
            category={category}
            onCategoryChange={handleCategoryChange}
            categoryDropdown={categoryDropdown}
            subCategory={subCategory}
            onSubCategoryChange={handleSubCategoryChange}
            subCategoryDropdown={subCategoryDropdown}
            selectedCategoryId={selectedCategoryId}
            state={state}
            onStateChange={handleStateChange}
            availableStates={availableStates}
            town={town}
            onTownChange={handleTownChange}
            availableTowns={availableTowns}
            isOtherTown={isOtherTown}
            setIsOtherTown={setIsOtherTown}
            customTown={customTown}
            setCustomTown={setCustomTown}
            citiesLoading={citiesLoading}
            stateIsoCode={stateIsoCode}
            citiesLoaded={citiesLoaded}
            fetchBackendCities={fetchBackendCities}
            isAddingCity={isAddingCity}
            handleCreateCity={handleCreateCity}
            hubName={hubName}
            setHubName={setHubName}
            heading={heading}
            setHeading={setHeading}
            pageSlug={pageSlug}
            setPageSlug={setPageSlug}
            locks={locks}
            setLocks={setLocks}
            applyTemplates={applyTemplates}
            introLine={introLine}
            setIntroLine={setIntroLine}
            descriptionHtml={descriptionHtml}
            setDescriptionHtml={setDescriptionHtml}
          />

          {/* 2. Retreat Filters Card */}
          <RetreatFiltersCard
            selectedFilters={selectedFilters}
            onFilterChange={handleFilterChange}
            atmosphereDropdown={atmosphereDropdown}
            featuresDropdown={featuresDropdown}
            surroundingAreasDropdown={surroundingAreasDropdown}
            thingsInAreaDropdown={thingsInAreaDropdown}
            mealPlanDropdown={mealPlanDropdown}
            languageDropdown={languageDropdown}
            matchingRetreats={matchingRetreats}
            sortOnPage={sortOnPage}
            setSortOnPage={setSortOnPage}
            retreatsList={retreatsList}
            openDrawer={openDrawer}
          />

          {/* 3. SEO Card */}
          <SeoCard
            metaTitle={metaTitle}
            setMetaTitle={setMetaTitle}
            indexSetting={indexSetting}
            setIndexSetting={setIndexSetting}
            metaDescription={metaDescription}
            setMetaDescription={setMetaDescription}
            canonicalUrl={canonicalUrl}
            targetKeyword={targetKeyword}
            setTargetKeyword={setTargetKeyword}
            pageSlug={pageSlug}
            setLocks={setLocks}
            showSnackbar={showSnackbar}
          />

          {/* 4. Detailed Content Card */}
          <DetailedContentCard
            detailedContentHtml={detailedContentHtml}
            setDetailedContentHtml={setDetailedContentHtml}
          />

          {/* 5. Suitability Items & FAQs */}
          <SuitabilityFaqsCard
            suitabilityItems={suitabilityItems}
            setSuitabilityItems={setSuitabilityItems}
            faqs={faqs}
            setFaqs={setFaqs}
            showSnackbar={showSnackbar}
          />
        </Box>

        {/* RIGHT SIDEBAR (340px) */}
        <Box>
          {/* Location Image Card */}
          <LocationImageCard
            previewImage={previewImage}
            imageAltText={imageAltText}
            setImageAltText={setImageAltText}
            handleImageChange={handleImageChange}
            handleRemoveImage={handleRemoveImage}
          />

          {/* Placement Card */}
          <PlacementCard
            pHome={pHome}
            setPHome={setPHome}
            pTab={pTab}
            setPTab={setPTab}
            pOrder={pOrder}
            setPOrder={setPOrder}
            isCategoryWise={isCategoryWise}
            setIsCategoryWise={setIsCategoryWise}
            isLocationWise={isLocationWise}
            setIsLocationWise={setIsLocationWise}
            isDateWise={isDateWise}
            setIsDateWise={setIsDateWise}
            relatedGroups={relatedGroups}
            town={town}
            state={state}
            subCategory={subCategory}
            category={category}
            removeRelHub={removeRelHub}
            openDrawer={openDrawer}
          />

          {/* Publish Card */}
          <PublishCard
            checklist={checklist}
            status={status}
            loading={loading}
            canPublish={canPublish}
            handleUnpublish={handleUnpublish}
            handleSave={handleSave}
            setModalOpen={setModalOpen}
            isEdit={isEdit}
            navigate={navigate}
          />
        </Box>
      </Box>

      {/* Slide-out Drawer for Viewing matching retreats */}
      <PinRetreatDrawer
        open={drawerOpen && drawerKind === 'list'}
        onClose={() => setDrawerOpen(false)}
        matchingRetreats={matchingRetreats}
        loading={retreatsLoading}
      />

      {/* Slide-out Drawer for Adding Related Hubs */}
      <HubsDrawer
        drawerOpen={drawerOpen && drawerKind === 'hubs'}
        setDrawerOpen={setDrawerOpen}
        drawerKind="hubs"
        allAvailableHubs={allAvailableHubs}
        addRelHub={addRelHub}
      />

      {/* Preview Modal */}
      <HubPreviewModal
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        pageSlug={pageSlug}
        category={category}
        state={state}
        town={town}
        heading={heading}
        introLine={introLine}
        atmosphereDropdown={atmosphereDropdown}
        featuresDropdown={featuresDropdown}
        descriptionHtml={descriptionHtml}
        detailedContentHtml={detailedContentHtml}
        matchingRetreats={matchingRetreats}
        pageName={pageName}
        placeName={placeName}
        relatedGroups={relatedGroups}
        suitabilityItems={suitabilityItems}
        faqs={faqs}
      />
    </Box>
  );
};

export default AddEditHubsLocation;
